/**
 * Normalize QMS API responses (ApiResponse + DataSet-style tables).
 */

export function getApiData(response) {
  if (!response || typeof response !== "object") return null;
  return response.data ?? response.Data ?? null;
}

export function normalizeSuccessStoryList(response) {
  const d = getApiData(response);
  if (Array.isArray(d?.SuccessStory)) return d.SuccessStory;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  return [];
}

export function firstSuccessStoryRow(response) {
  const list = normalizeSuccessStoryList(response);
  if (list.length) return list[0];
  const d = getApiData(response);
  if (d && typeof d === "object" && !Array.isArray(d)) {
    if ("Success" in d || "success" in d) return d;
  }
  return null;
}

export function isApiSuccess(response, procRow) {
  if (procRow != null) {
    return Number(procRow.Success ?? procRow.success) === 1 || procRow.Success === true;
  }

  const topLevelSuccess = response?.success ?? response?.Success;
  if (topLevelSuccess !== undefined && topLevelSuccess !== null) {
    return Number(topLevelSuccess) === 1 || topLevelSuccess === true;
  }

  const row = firstSuccessStoryRow(response);
  if (row != null) {
    return Number(row.Success ?? row.success) === 1 || row.Success === true;
  }

  return false;
}

export function apiMessage(response, procRow) {
  const row = procRow ?? firstSuccessStoryRow(response);
  return (
    row?.MSG ||
    row?.Message ||
    response?.message ||
    response?.Message ||
    ""
  );
}

export function formatVaultApiError(message) {
  const text = String(message || "").trim();
  if (text === "No data returned from My Vault.") {
    return "The server returned no data. Re-run QMS_StoredProcedures.sql on the API database and restart the API.";
  }
  if (text.includes("GetDocumentControlList returned no data")) {
    return text;
  }
  if (text.includes("PublishDocument returned no data")) {
    return text;
  }
  return text || "Request failed.";
}

/** Read a field from an API row with PascalCase / camelCase aliases. */
export function pickApiRowValue(row, ...keys) {
  if (!row || typeof row !== "object") return null;
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && String(row[key]).trim() !== "") {
      return row[key];
    }
    const match = Object.keys(row).find(
      (candidate) => candidate.toLowerCase() === String(key || "").toLowerCase(),
    );
    if (
      match &&
      row[match] !== undefined &&
      row[match] !== null &&
      String(row[match]).trim() !== ""
    ) {
      return row[match];
    }
  }
  return null;
}

function normalizeApiTableName(value) {
  return String(value || "").trim().toLowerCase();
}

export function unwrapApiTableRows(value) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return null;
  if (Array.isArray(value.Rows)) return value.Rows;
  if (Array.isArray(value.rows)) return value.rows;
  return null;
}

/** Collect every row-set exposed on a QMS ApiResponse payload. */
export function listApiTableRowSets(response) {
  const d = getApiData(response);
  if (!d || typeof d !== "object") return [];

  const sets = [];
  const seen = new Set();

  const pushRows = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const key = `${rows.length}:${Object.keys(rows[0] || {}).sort().join(",")}`;
    if (seen.has(key)) return;
    seen.add(key);
    sets.push(rows);
  };

  if (Array.isArray(d.Tables)) {
    d.Tables.forEach((table) => pushRows(unwrapApiTableRows(table)));
  } else if (d.Tables && typeof d.Tables === "object") {
    Object.values(d.Tables).forEach((value) => pushRows(unwrapApiTableRows(value)));
  }

  Object.entries(d).forEach(([key, value]) => {
    if (key === "Tables") return;
    pushRows(unwrapApiTableRows(value));
    if (Array.isArray(value)) pushRows(value);
  });

  return sets;
}

function isTrainingRoleTableRow(row) {
  if (!row || typeof row !== "object") return false;
  const staffId = pickApiRowValue(row, "StaffId", "staffId");
  const roleId = pickApiRowValue(row, "RoleId", "RoleIdRef", "roleId");
  const displayName = pickApiRowValue(row, "UserName", "StaffName", "userName", "staffName");
  return Boolean(staffId && roleId && !displayName);
}

/** Raw StaffTrainingAdditionalRoles rows from any QMS ApiResponse shape. */
export function getStaffTrainingAdditionalRolesTable(response) {
  const namedRows = getApiTable(response, "StaffTrainingAdditionalRoles");
  if (namedRows.length) return namedRows;

  const d = getApiData(response);
  if (!d || typeof d !== "object") return [];

  const tables = Array.isArray(d.Tables)
    ? d.Tables.map((table, index) => ({
        name: table?.TableName || table?.tableName || `Table${index}`,
        rows: unwrapApiTableRows(table) || [],
      }))
    : d.Tables && typeof d.Tables === "object"
      ? Object.entries(d.Tables).map(([name, value]) => ({
          name,
          rows: unwrapApiTableRows(value) || [],
        }))
      : [];

  for (const table of tables) {
    if (String(table.name || "").toLowerCase() === "stafftrainingadditionalroles") {
      if (table.rows.length) return table.rows;
    }
  }

  for (const table of tables) {
    if (table.rows.some(isTrainingRoleTableRow)) {
      return table.rows.filter(isTrainingRoleTableRow);
    }
  }

  for (const value of Object.values(d)) {
    const rows = unwrapApiTableRows(value);
    if (Array.isArray(rows) && rows.some(isTrainingRoleTableRow)) {
      return rows.filter(isTrainingRoleTableRow);
    }
  }

  return [];
}

/** Normalized training-role rows for My Learning assignment grouping. */
export function getStaffTrainingAdditionalRolesFromResponse(response) {
  return getStaffTrainingAdditionalRolesTable(response)
    .map((row) => ({
      StaffId: pickApiRowValue(row, "StaffId", "staffId"),
      RoleId: pickApiRowValue(row, "RoleId", "RoleIdRef", "roleId"),
      Roles: pickApiRowValue(row, "Roles", "roles") || "",
    }))
    .filter((row) => row.StaffId && row.RoleId);
}

function getRowsFromDatasetTable(table) {
  return unwrapApiTableRows(table);
}

function findDatasetTableRows(d, tableName) {
  const target = normalizeApiTableName(tableName);

  if (Array.isArray(d?.Tables)) {
    const named = d.Tables.find(
      (table) =>
        normalizeApiTableName(table?.TableName || table?.Name || table?.tableName) === target,
    );
    const namedRows = getRowsFromDatasetTable(named);
    if (Array.isArray(namedRows)) return namedRows;
  }

  if (d?.Tables && typeof d.Tables === "object" && !Array.isArray(d.Tables)) {
    const match = Object.keys(d.Tables).find(
      (key) => normalizeApiTableName(key) === target,
    );
    if (match) {
      const tableRows = unwrapApiTableRows(d.Tables[match]);
      if (Array.isArray(tableRows)) return tableRows;
    }
  }

  return null;
}

/** Extract a named DataSet table from QMS ApiResponse (e.g. DocumentLibraryDocuments). */
export function hasApiTable(response, tableName) {
  const d = getApiData(response);
  if (!d || typeof d !== "object") return false;

  if (Array.isArray(d[tableName])) return true;

  const directRows = unwrapApiTableRows(d[tableName]);
  if (Array.isArray(directRows)) return true;

  const match = Object.keys(d).find(
    (key) => normalizeApiTableName(key) === normalizeApiTableName(tableName),
  );
  if (match) {
    const matchedRows = unwrapApiTableRows(d[match]);
    if (Array.isArray(matchedRows)) return true;
  }

  return Array.isArray(findDatasetTableRows(d, tableName));
}

export function getApiTable(response, tableName) {
  const d = getApiData(response);
  if (!d || typeof d !== "object") return [];

  const directRows = unwrapApiTableRows(d[tableName]);
  if (Array.isArray(directRows)) return directRows;

  const match = Object.keys(d).find(
    (key) => normalizeApiTableName(key) === normalizeApiTableName(tableName),
  );
  if (match) {
    const matchedRows = unwrapApiTableRows(d[match]);
    if (Array.isArray(matchedRows)) return matchedRows;
  }

  const datasetRows = findDatasetTableRows(d, tableName);
  if (Array.isArray(datasetRows)) return datasetRows;

  return [];
}

export function getApiTableRow(response, ...tableNames) {
  for (const tableName of tableNames) {
    const row = getApiTable(response, tableName)[0];
    if (row) return row;
  }
  return null;
}

export function getPaginationInfo(response) {
  const rows = getApiTable(response, "PaginationInfo");
  return rows[0] || { TotalCount: 0, PageNumber: 1, PageSize: 25 };
}
